import { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, User, CheckCircle2, Link2, X, Settings, LockKeyhole } from "lucide-react";

const starterQuestions = [
  "วันนี้มีคนเข้าร้านกี่คน",
  "เทียบคนเข้าร้านกับเมื่อวาน",
  "ช่วงเวลาไหนคนเข้าร้านมากที่สุด",
  "มีสมาชิกและ VIP กี่คน",
];

export default function MiniAI() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "สวัสดีครับ ผมคือ PIXELS Mini AI ลองถามข้อมูลภาพรวมร้านของคุณได้เลยครับ",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [accessChecking, setAccessChecking] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [lineConfig, setLineConfig] = useState(null);
  const [linkCode, setLinkCode] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [showLineSettings, setShowLineSettings] = useState(false);
  const [lineSaving, setLineSaving] = useState(false);
  const [lineForm, setLineForm] = useState({
    channel_secret: "",
    channel_access_token: "",
    bot_basic_id: "",
  });
  const endRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const canConfigureLine = currentUser.is_main || ["owner", "admin"].includes(currentUser.role);

  useEffect(() => {
    let active = true;
    const loadAccess = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
        const statusResponse = await fetch("/api/ai/status", { headers });
        const statusJson = await statusResponse.json().catch(() => ({}));
        if (!statusResponse.ok) {
          if (active) setAccessDenied(statusResponse.status === 403);
          return;
        }
        if (!active) return;
        setStatus(statusJson);
        setAccessDenied(false);
        const lineResponse = await fetch("/api/line/config", { headers });
        const config = lineResponse.ok ? await lineResponse.json() : null;
        if (!active) return;
        setLineConfig(config);
        if (config?.bot_basic_id) {
          setLineForm(current => ({ ...current, bot_basic_id: config.bot_basic_id }));
        }
      } catch {
        if (active) setStatus(null);
      } finally {
        if (active) setAccessChecking(false);
      }
    };
    loadAccess();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const ask = async (text) => {
    const question = text.trim();
    if (!question || loading) return;

    setMessages(current => [...current, { role: "user", text: question }]);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch("/api/ai/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ message: question }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || "ไม่สามารถตอบคำถามได้");
      setMessages(current => [
        ...current,
        { role: "assistant", text: json.answer, meta: `ข้อมูลช่วง ${json.period?.label || json.data_date} · ${json.scope}` },
      ]);
    } catch (error) {
      setMessages(current => [
        ...current,
        { role: "assistant", text: error.message, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    ask(input);
  };

  const createLineLink = async () => {
    setLinkLoading(true);
    try {
      const response = await fetch("/api/line/link-code", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || "สร้างรหัสไม่สำเร็จ");
      setLinkCode(json);
    } catch (error) {
      setMessages(current => [...current, { role: "assistant", text: error.message, error: true }]);
    } finally {
      setLinkLoading(false);
    }
  };

  const saveLineConfig = async (event) => {
    event.preventDefault();
    setLineSaving(true);
    try {
      const response = await fetch("/api/line/config", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(lineForm),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || "บันทึก LINE ไม่สำเร็จ");
      setLineConfig({
        configured: true,
        status: "active",
        bot_basic_id: lineForm.bot_basic_id,
        webhook_url: json.webhook_url,
      });
      setLineForm(current => ({ ...current, channel_secret: "", channel_access_token: "" }));
      setShowLineSettings(false);
      setMessages(current => [...current, {
        role: "assistant",
        text: `ตั้งค่า LINE สำเร็จแล้วครับ\nนำ Webhook URL นี้ไปใส่ใน LINE Developers Console:\n${json.webhook_url}`,
      }]);
    } catch (error) {
      setMessages(current => [...current, { role: "assistant", text: error.message, error: true }]);
    } finally {
      setLineSaving(false);
    }
  };

  if (accessChecking) {
    return <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-50"><div className="h-10 w-10 animate-spin rounded-full border-4 border-cyan-100 border-t-cyan-500" /></div>;
  }

  if (accessDenied) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl shadow-slate-200/50">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-600"><LockKeyhole size={28} /></span>
          <h1 className="mt-5 text-xl font-bold text-slate-900">Mini AI ยังไม่ได้เปิดใช้งาน</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">บริการ AI และการเชื่อมต่อ LINE ต้องได้รับการเปิดใช้งานจาก PIXELS SuperAdmin ก่อน กรุณาติดต่อผู้ดูแลระบบ</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-slate-50 via-white to-cyan-50/60 p-4 sm:p-6">
      <div className="mx-auto flex h-[calc(100vh-7rem)] max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">
        <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-950 px-5 py-4 text-white">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500">
            <Sparkles size={22} />
          </div>
          <div>
            <h1 className="font-bold">PIXELS Mini AI</h1>
            <p className="text-xs text-slate-300">ผู้ช่วยวิเคราะห์ข้อมูลร้านแบบ Read-only</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs text-slate-200">
            <CheckCircle2 size={13} className="text-emerald-400" />
            {status ? "AI ของเรา · ฟรี" : "กำลังเชื่อมต่อ"}
          </div>
          <button
            type="button"
            onClick={createLineLink}
            disabled={linkLoading || !lineConfig?.configured}
            className="flex items-center gap-1.5 rounded-lg bg-[#06C755] px-2.5 py-2 text-xs font-semibold text-white transition hover:bg-[#05b74e] disabled:cursor-not-allowed disabled:opacity-50 sm:px-3"
            title={!lineConfig?.configured ? "ลูกค้ารายนี้ยังไม่ได้ตั้งค่า LINE" : "เชื่อมบัญชี LINE"}
          >
            <Link2 size={14} />
            <span className="hidden sm:inline">{linkLoading ? "กำลังสร้าง..." : "เชื่อม LINE"}</span>
          </button>
          {canConfigureLine && (
            <button
              type="button"
              onClick={() => setShowLineSettings(current => !current)}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-slate-200 transition hover:bg-white/20"
              title="ตั้งค่า LINE OA ของลูกค้ารายนี้"
            >
              <Settings size={15} />
            </button>
          )}
        </div>

        {showLineSettings && (
          <form onSubmit={saveLineConfig} className="relative border-b border-cyan-200 bg-cyan-50 px-5 py-4">
            <button type="button" onClick={() => setShowLineSettings(false)} className="absolute right-3 top-3 rounded-md p-1 text-slate-500 hover:bg-cyan-100" aria-label="ปิด"><X size={16} /></button>
            <h2 className="font-semibold text-slate-900">ตั้งค่า LINE OA สำหรับ {currentUser.company_name || currentUser.app_id}</h2>
            <p className="mt-1 text-xs text-slate-500">Credential จะถูกเข้ารหัสก่อนเก็บและจะไม่แสดงกลับมาบนหน้าจออีก</p>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <input
                required
                type="password"
                value={lineForm.channel_secret}
                onChange={event => setLineForm({ ...lineForm, channel_secret: event.target.value })}
                placeholder="Channel Secret"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-cyan-500"
              />
              <input
                required
                type="password"
                value={lineForm.channel_access_token}
                onChange={event => setLineForm({ ...lineForm, channel_access_token: event.target.value })}
                placeholder="Channel Access Token"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-cyan-500"
              />
              <input
                value={lineForm.bot_basic_id}
                onChange={event => setLineForm({ ...lineForm, bot_basic_id: event.target.value })}
                placeholder="Basic ID เช่น @pixels"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-cyan-500"
              />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button disabled={lineSaving} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50">
                {lineSaving ? "กำลังเข้ารหัสและบันทึก..." : "บันทึก LINE ของลูกค้ารายนี้"}
              </button>
              {lineConfig?.webhook_url && <span className="break-all text-xs text-slate-500">Webhook: {lineConfig.webhook_url}</span>}
            </div>
          </form>
        )}

        {linkCode && (
          <div className="relative border-b border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-950">
            <button onClick={() => setLinkCode(null)} className="absolute right-3 top-3 rounded-md p-1 text-emerald-700 hover:bg-emerald-100" aria-label="ปิด"><X size={16} /></button>
            <p className="font-semibold">เปิด LINE OA แล้วส่งข้อความนี้ภายใน 10 นาที</p>
            <button
              onClick={() => navigator.clipboard?.writeText(linkCode.command)}
              className="mt-2 rounded-lg border border-emerald-300 bg-white px-4 py-2 font-mono text-lg font-bold tracking-wider text-emerald-700 shadow-sm"
              title="กดเพื่อคัดลอก"
            >
              {linkCode.command}
            </button>
            <p className="mt-1 text-xs text-emerald-700">กดที่รหัสเพื่อคัดลอก แล้ววางในห้องแชต LINE ของ PIXELS</p>
          </div>
        )}

        <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
          {messages.map((message, index) => (
            <div key={index} className={`flex gap-3 ${message.role === "user" ? "justify-end" : ""}`}>
              {message.role === "assistant" && (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700">
                  <Bot size={17} />
                </div>
              )}
              <div className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-6 ${
                message.role === "user"
                  ? "rounded-br-md bg-slate-900 text-white"
                  : message.error
                    ? "rounded-bl-md bg-red-50 text-red-700"
                    : "rounded-bl-md border border-slate-200 bg-slate-50 text-slate-700"
              }`}>
                <p className="whitespace-pre-wrap">{message.text}</p>
                {message.meta && <p className="mt-2 border-t border-slate-200 pt-2 text-[11px] text-slate-400">{message.meta}</p>}
              </div>
              {message.role === "user" && (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-200 text-slate-600">
                  <User size={17} />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700"><Bot size={17} /></div>
              <div className="flex gap-1 rounded-2xl rounded-bl-md bg-slate-100 px-4 py-4">
                {[0, 1, 2].map(item => <span key={item} className="h-2 w-2 animate-pulse rounded-full bg-cyan-500" style={{ animationDelay: `${item * 150}ms` }} />)}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length <= 1 && (
          <div className="flex gap-2 overflow-x-auto px-4 pb-3 sm:px-6">
            {starterQuestions.map(question => (
              <button key={question} onClick={() => ask(question)} className="shrink-0 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-medium text-cyan-800 transition hover:bg-cyan-100">
                {question}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="border-t border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-end gap-2 rounded-2xl border border-slate-300 bg-slate-50 p-2 focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-100">
            <textarea
              rows={1}
              maxLength={500}
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit(event);
                }
              }}
              placeholder="ถามข้อมูลร้าน เช่น วันนี้มีลูกค้าเข้ากี่คน..."
              className="max-h-28 min-h-11 flex-1 resize-none bg-transparent px-3 py-2 text-sm outline-none"
            />
            <button disabled={!input.trim() || loading} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-500 text-white transition hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-40" aria-label="ส่งคำถาม">
              <Send size={18} />
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-slate-400">PIXELS Mini AI ทำงานบน Server ของเรา · ไม่มีค่า API · ไม่แก้ไขข้อมูลในระบบ</p>
        </form>
      </div>
    </div>
  );
}
